import { useMemo } from 'react';
import {
  Crown, Wallet, CircleUser, Bell, Megaphone, Trophy, MessagesSquare,
  Sparkles, Gift, Info, ArrowDownCircle, ArrowUpCircle, Play, ChevronRight,
} from 'lucide-react';

/**
 * Full-fidelity live preview of the player home.
 *
 * Renders a faithful mock of every major surface (header / banners /
 * Explore tile / My Games card / chat FAB / nav) wrapped in a `.player-app`
 * div whose CSS variables are derived from the *merged* theme draft — so
 * any change in the editor recolours every element instantly without
 * touching the real `/app` page.
 *
 * The wrapper carries a unique `data-vl-preview-scope` attribute and we
 * use inline CSS-var declarations (instead of `:root`) so the editor's
 * surrounding chrome stays unchanged.
 */
export default function PlayerHomePreview({ theme }) {
  // Build the inline CSS-var map from the merged draft theme.
  const cssVars = useMemo(() => {
    const out = {};
    if (!theme) return out;
    for (const [k, v] of Object.entries(theme)) {
      if (typeof v !== 'string' || !v.length) continue;
      if (['custom_css', 'logo_url', 'intro_logo_url', 'brand_name', 'tagline'].includes(k)) continue;
      out['--vl-' + k.replace(/_/g, '-')] = v;
    }
    return out;
  }, [theme]);

  const t = theme || {};
  const brandName = (t.brand_name || 'Mystic Reels').toUpperCase();
  const tagline = t.tagline || 'Player Lounge';
  const logoUrl = t.logo_url || '';

  return (
    <div
      className="player-app rounded-3xl overflow-hidden border border-white/10 select-none"
      style={{
        ...cssVars,
        background: t.bg || '#000000',
        color: t.text_primary || '#fff',
        height: '760px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Custom CSS injected for this preview only.
      }}
      data-vl-preview-scope
      data-testid="player-home-preview"
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between gap-2"
        style={{ background: t.bg || '#000000', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-2">
          <div className="orb" style={logoUrl ? { background: 'transparent', overflow: 'hidden', padding: 0 } : {}}>
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="w-full h-full object-contain" />
            ) : (
              <Crown className="w-7 h-7" style={{ color: '#02140A' }} />
            )}
          </div>
          <div>
            <p className="display text-sm font-extrabold uppercase leading-tight" style={{ color: t.text_primary || '#fff' }}>{brandName}</p>
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: t.text_muted || 'rgba(255,255,255,0.65)' }}>{tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Balance pill */}
          <div className="rounded-xl px-3 py-1.5 flex items-center gap-2"
            style={{ background: 'rgba(168,85,247,0.16)', border: '1px solid rgba(168,85,247,0.4)' }}>
            <Wallet className="w-3.5 h-3.5" style={{ color: t.neon || '#22FF88' }} />
            <div className="leading-tight">
              <p className="text-[8px] uppercase tracking-wider" style={{ color: t.text_muted || 'rgba(255,255,255,0.55)' }}>Balance</p>
              <p className="display text-xs font-extrabold" style={{ color: t.neon || '#22FF88', textShadow: `0 0 10px ${t.neon || '#22FF88'}55` }}>$0.00</p>
            </div>
            <span className="text-[8px] font-extrabold px-1 py-0.5 rounded" style={{ background: '#FACC15', color: '#3F2A00' }}>0 FP</span>
          </div>
          {/* Profile pill */}
          <div className="rounded-xl px-2 py-1 flex items-center gap-1.5"
            style={{ background: 'rgba(56,189,248,0.18)', border: '1px solid rgba(56,189,248,0.45)' }}>
            <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #38BDF8, #0EA5E9)' }}>
              <CircleUser className="w-3.5 h-3.5 text-white" />
            </span>
            <div className="leading-tight">
              <p className="text-[8px] uppercase tracking-wider" style={{ color: '#7DD3FC' }}>Profile</p>
              <p className="text-[10px] font-bold" style={{ color: '#fff' }}>player_demo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Header icons row */}
      <div className="px-4 py-2 flex items-center gap-2 border-b border-white/5">
        <PreviewHeaderIcon icon={Bell} color="#22D3EE" badge="3" />
        <PreviewHeaderIcon icon={Megaphone} color="#FFD93D" />
        <PreviewHeaderIcon icon={Trophy} color="#F97316" />
        <div className="flex-1" />
        <button className="text-[11px] display font-bold rounded-md px-2 py-1"
          style={{ background: t.neon ? `${t.neon}20` : 'rgba(34,255,136,0.16)', color: t.neon || '#22FF88', border: `1px solid ${t.neon || '#22FF88'}55` }}>
          + Add Game
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        style={{ background: t.bg || '#000000' }}>
        {/* Banner */}
        <div className="rounded-2xl px-4 py-3 flex items-center gap-3"
          style={{ background: 'linear-gradient(135deg, #5B2BFF 0%, rgba(91,43,255,0.4) 60%, transparent 100%)', border: '1px solid rgba(244,180,0,0.45)' }}>
          <Sparkles className="w-6 h-6 text-yellow-300" />
          <div className="flex-1 min-w-0">
            <p className="display text-sm font-extrabold text-white">We're Here to Help You 24/7</p>
            <p className="text-[11px] text-white/80 truncate">Our support team is online around the clock.</p>
          </div>
        </div>

        {/* Explore & Earn — Referral hot tile */}
        <button
          className="w-full rounded-2xl p-3 flex items-center gap-3 text-left"
          style={{
            background: `linear-gradient(135deg, ${t.page_referral_accent || '#FF6B00'} 0%, #FF1A1A 100%)`,
            boxShadow: `0 6px 18px ${t.page_referral_accent ? `${t.page_referral_accent}55` : 'rgba(255,107,0,0.35)'}`,
          }}>
          <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="display text-sm font-extrabold text-white">Explore &amp; Earn</p>
            <p className="text-[11px] text-white/85">Freeplay bonuses and rewards</p>
          </div>
          <ChevronRight className="w-4 h-4 text-white" />
        </button>

        {/* My Games heading */}
        <div className="pt-1">
          <h3 className="display text-base font-extrabold text-white">My Games</h3>
          <p className="text-[11px]" style={{ color: t.neon || '#22FF88' }}>Your saved game accounts</p>
        </div>

        {/* Game card with all 4 action buttons */}
        <div className="rounded-2xl p-4 bg-white/95 text-[#0B0F0C]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #4338CA)' }}>
              <Play className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="display font-extrabold text-sm">Demo Game</p>
              <p className="text-[11px] text-gray-500">Username: <span className="font-mono">demo_player</span></p>
            </div>
            <Sparkles className="w-4 h-4" style={{ color: t.neon || '#22FF88' }} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button className="display rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 font-extrabold text-xs"
              style={{
                background: `linear-gradient(180deg, ${t.btn_add_credits_from || '#5B2BFF'}, ${t.btn_add_credits_to || '#3614C9'})`,
                color: t.btn_add_credits_text || '#fff',
                boxShadow: `0 6px 16px ${t.btn_add_credits_from ? `${t.btn_add_credits_from}55` : 'rgba(91,43,255,0.4)'}`,
              }}>
              <ArrowDownCircle className="w-3.5 h-3.5" />
              Add Credits
            </button>
            <button className="display rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 font-extrabold text-xs"
              style={{
                background: `linear-gradient(180deg, ${t.btn_withdraw_from || '#FF8A3D'}, ${t.btn_withdraw_to || '#E63946'})`,
                color: t.btn_withdraw_text || '#fff',
                boxShadow: `0 6px 16px ${t.btn_withdraw_from ? `${t.btn_withdraw_from}55` : 'rgba(230,57,70,0.4)'}`,
              }}>
              <ArrowUpCircle className="w-3.5 h-3.5" />
              Withdraw
            </button>
          </div>
          <button className="display w-full rounded-xl py-2 px-3 flex items-center justify-center gap-1.5 font-extrabold text-xs mb-2"
            style={{
              background: `linear-gradient(180deg, ${t.btn_warning_from || '#FFE873'}, ${t.btn_warning_to || '#F4B400'})`,
              color: t.btn_warning_text || '#1A1100',
              border: `1px solid ${t.btn_warning_to || '#F4B400'}99`,
            }}>
            <Info className="w-3.5 h-3.5" />
            Cashout Rules
          </button>
          <button className="display w-full rounded-xl py-2.5 flex items-center justify-center gap-2 font-extrabold text-sm"
            style={{
              background: `linear-gradient(180deg, ${t.btn_primary_from || '#22FF88'}, ${t.btn_primary_to || '#00C853'})`,
              color: t.btn_primary_text || '#02140A',
              boxShadow: `0 0 0 1px ${t.btn_primary_from || '#22FF88'}55, 0 10px 24px ${t.btn_primary_from || '#22FF88'}66`,
            }}>
            <Play className="w-4 h-4" />
            Play Now
          </button>
        </div>

        {/* Page-accent strip */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { name: 'Roulette', color: t.page_roulette_accent || '#EF4444' },
            { name: 'Market',   color: t.page_marketplace_accent || '#F59E0B' },
            { name: 'Tasks',    color: t.page_tasks_accent || '#10B981' },
            { name: 'Tiers',    color: t.page_tiers_accent || '#8B5CF6' },
          ].map((p) => (
            <div key={p.name} className="rounded-xl p-2 flex flex-col items-center text-center"
              style={{ background: `${p.color}18`, border: `1px solid ${p.color}55` }}>
              <span className="w-6 h-6 rounded-md" style={{ background: p.color }} />
              <p className="text-[10px] font-bold mt-1" style={{ color: p.color }}>{p.name}</p>
            </div>
          ))}
        </div>

        {/* Sample notification */}
        <div className="rounded-lg px-3 py-2.5"
          style={{ background: `${t.neon || '#22FF88'}14`, border: `1px solid ${t.neon || '#22FF88'}55` }}>
          <div className="flex items-start gap-2">
            <span className="mt-1 w-2 h-2 rounded-full shrink-0"
              style={{ background: t.neon || '#22FF88', boxShadow: `0 0 8px ${t.neon || '#22FF88'}` }} />
            <div className="flex-1">
              <p className="text-xs font-bold text-white">Welcome bonus credited</p>
              <p className="text-[10px] text-white/70">$5 freeplay added to your wallet · 2 min ago</p>
            </div>
          </div>
        </div>
      </div>

      {/* Live chat FAB (fixed inside preview) */}
      <button
        className="absolute right-4 bottom-4 w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          background: `linear-gradient(180deg, ${t.btn_primary_from || '#22FF88'}, ${t.btn_primary_to || '#00C853'})`,
          color: t.btn_primary_text || '#02140A',
          boxShadow: `0 8px 24px ${t.btn_primary_from || '#22FF88'}55`,
        }}>
        <MessagesSquare className="w-5 h-5" />
      </button>
    </div>
  );
}

function PreviewHeaderIcon({ icon: Icon, color, badge }) {
  return (
    <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-lg"
      style={{ background: `${color}15`, border: `1px solid ${color}55` }}>
      <Icon className="w-4 h-4" style={{ color }} />
      {badge && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-extrabold flex items-center justify-center"
          style={{ background: '#EC4899', color: '#fff' }}>
          {badge}
        </span>
      )}
    </span>
  );
}
